// AI Step 7: Review & Sign-Off
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../../../../components/ui/alert';
import { FileWarning, PenLine, ExternalLink } from 'lucide-react';

export default function Step7Review({ formData, updateField }) {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Review & Sign-Off</h2>
        <p className="text-muted-foreground mt-1">Final review and approval of the AI assessment.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4 text-center"><p className="text-3xl font-bold text-blue-500">{Math.round(formData.initial_risk_score || 0)}</p><p className="text-xs text-muted-foreground">Initial Risk</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-3xl font-bold text-green-500">{Math.round(formData.residual_risk_score || 0)}</p><p className="text-xs text-muted-foreground">Residual Risk</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-3xl font-bold">{(formData.technical_controls || []).filter(c => c.implemented).length}</p><p className="text-xs text-muted-foreground">Tech Controls</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-3xl font-bold">{formData.affected_populations.length}</p><p className="text-xs text-muted-foreground">Affected Groups</p></CardContent></Card>
      </div>

      {formData.dpia_recommended && (
        <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <FileWarning className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-700 dark:text-amber-400">DPIA Recommended</AlertTitle>
          <AlertDescription className="text-amber-600 dark:text-amber-300">
            Based on the risk assessment, a Data Protection Impact Assessment is recommended.
            {formData.linked_dpia_id ? (
              <span> Linked DPIA: {formData.linked_dpia_id}</span>
            ) : (
              <Button variant="link" className="p-0 h-auto ml-2" onClick={() => navigate('/risk/dpia/new')}>
                Create DPIA <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><PenLine className="h-4 w-4 text-primary" />Sign-Off</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Review Summary</Label>
            <Textarea value={formData.review_summary || ''} onChange={e => updateField('review_summary', e.target.value)} placeholder="Final summary of the AI assessment findings and decision..." className="mt-1.5" rows={4} />
          </div>
          <div>
            <Label>NIST AI RMF Alignment Notes</Label>
            <Textarea value={formData.nist_alignment_notes || ''} onChange={e => updateField('nist_alignment_notes', e.target.value)} placeholder="Notes on alignment with NIST AI RMF functions..." className="mt-1.5" rows={2} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><Label>Approved By <span className="text-destructive">*</span></Label><Input value={formData.approved_by || ''} onChange={e => updateField('approved_by', e.target.value)} placeholder="Name of approver" className="mt-1.5" data-testid="ai-approved-by" /></div>
            <div><Label>Role</Label><Input value={formData.approval_role || ''} onChange={e => updateField('approval_role', e.target.value)} placeholder="e.g., AI Governance Lead" className="mt-1.5" /></div>
            <div><Label>Approval Date <span className="text-destructive">*</span></Label><Input type="date" value={formData.approval_date || ''} onChange={e => updateField('approval_date', e.target.value)} className="mt-1.5" data-testid="ai-approval-date" /></div>
          </div>
          <div><Label>Next Review Date</Label><Input type="date" value={formData.next_review_date || ''} onChange={e => updateField('next_review_date', e.target.value)} className="mt-1.5 w-48" /></div>
          <div className="pt-4">
            <Label>Status</Label>
            <Select value={formData.status} onValueChange={v => updateField('status', v)}>
              <SelectTrigger className="mt-1.5 w-48" data-testid="ai-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem><SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem><SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem><SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
